// src/pages/application/SubscribePage.js
// INITIAL SUBSCRIPTION PAGE - Users see this after registration
// Clean, professional design with principle colors only

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { businessAPI } from '../../services/businessAPI';

export default function SubscribePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const handleSelectPlan = async (tier) => {
    try {
      setLoading(true);
      console.log(`🚀 Selecting ${tier} plan...`);

      // TODO: Call backend API to create Stripe checkout session
      // const response = await businessAPI.createCheckoutSession(tier);
      // window.location.href = response.checkoutUrl; // Redirect to Stripe (same tab for initial subscription)

      // Placeholder: Simulate successful subscription
      alert(`Redirecting to Stripe payment...\n\nPlan: ${tiers[tier].name}\nPrice: £${tiers[tier].price}/month`);

      // After successful payment, user will be redirected back to dashboard
      // navigate('/dashboard');

    } catch (error) {
      console.error('❌ Subscription failed:', error);
      alert('Failed to start subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const tiers = {
    diy: {
      id: 'diy',
      name: 'DIY Starter',
      price: 29,
      description: 'Marketing Studio + Manual Scheduling',
      icon: 'fas fa-seedling',
      color: 'gray',
      features: [
        'Complete Marketing Studio',
        'AI Content Ideas Generator',
        'Content Calendar & Templates',
        '30 or 90-day SMART Goals',
        'Social media connection',
        'Manual post scheduling',
        'Performance Analytics',
        'Community support',
        '30-day FREE trial'
      ]
    },
    assisted: {
      id: 'assisted',
      name: 'Assisted Growth',
      price: 129,
      description: 'CRM + Auto-Posting + Expert Help',
      icon: 'fas fa-rocket',
      color: 'blue',
      badge: 'MOST POPULAR',
      popular: true,
      features: [
        'Everything in DIY Starter',
        'Full CRM Access (up to 500 customers)',
        '8 AI auto-posts/month',
        '30 or 90-day SMART Goals',
        'Monthly strategy call (30 mins)',
        'Automated monthly reports',
        'Keyword & SEO suggestions',
        'Email support (48hr response)'
      ]
    },
    managed: {
      id: 'managed',
      name: 'Fully Managed VIP',
      price: 449,
      description: 'Done-for-you marketing & CRM',
      icon: 'fas fa-crown',
      color: 'green',
      badge: 'PREMIUM',
      features: [
        'Everything in Assisted Growth',
        'Unlimited CRM customers',
        '20 posts/month (16 AI + 4 custom)',
        'Full CRM management by our team',
        'Bi-weekly strategy calls',
        'Google My Business management',
        'Basic Local SEO & citations',
        'Reputation & review management',
        'Priority support (24hr response)',
        'White-label reports'
      ]
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img
                src="/website/logo/dt.png"
                alt="Digital Trent"
                className="h-10 w-10 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Digital Trent</h1>
                <p className="text-xs text-gray-500">Choose Your Plan</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 py-12 px-6">
        <div className="max-w-7xl mx-auto">

          {/* Hero Section */}
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              Choose Your Marketing Plan
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Start growing your business with AI-powered marketing tools. Choose the plan that fits your needs.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {Object.values(tiers).map((tier) => (
              <div
                key={tier.id}
                className={`bg-white rounded-xl border-2 transition-all ${
                  tier.popular
                    ? 'border-blue-600 shadow-lg scale-105'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                }`}
              >
                {/* Card Header */}
                <div className={`p-6 border-b border-gray-100 ${
                  tier.popular ? 'bg-blue-50' : tier.color === 'green' ? 'bg-green-50' : 'bg-gray-50'
                }`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      tier.color === 'blue' ? 'bg-blue-600' :
                      tier.color === 'green' ? 'bg-green-600' :
                      'bg-gray-600'
                    }`}>
                      <i className={`${tier.icon} text-white text-xl`}></i>
                    </div>
                    {tier.badge && (
                      <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${
                        tier.color === 'blue' ? 'bg-blue-600 text-white' :
                        tier.color === 'green' ? 'bg-green-600 text-white' :
                        'bg-gray-600 text-white'
                      }`}>
                        {tier.badge}
                      </span>
                    )}
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-1">{tier.name}</h3>
                  <p className="text-sm text-gray-600 mb-4">{tier.description}</p>

                  <div className="flex items-baseline">
                    <span className="text-4xl font-bold text-gray-900">£{tier.price}</span>
                    <span className="text-base text-gray-600 ml-2">/month</span>
                  </div>
                </div>

                {/* Features */}
                <div className="p-6">
                  <ul className="space-y-3 mb-6">
                    {tier.features.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <i className={`fas fa-check-circle text-base mt-0.5 ${
                          tier.color === 'blue' ? 'text-blue-600' :
                          tier.color === 'green' ? 'text-green-600' :
                          'text-gray-600'
                        }`}></i>
                        <span className="text-sm text-gray-700 leading-relaxed">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA Button */}
                  <button
                    onClick={() => handleSelectPlan(tier.id)}
                    disabled={loading}
                    className={`w-full py-3 text-sm font-semibold rounded-lg transition-all ${
                      tier.popular
                        ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg'
                        : tier.color === 'green'
                        ? 'bg-green-600 text-white hover:bg-green-700 shadow-md hover:shadow-lg'
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                    } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {loading ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        Processing...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-credit-card mr-2"></i>
                        Get Started
                      </>
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Trust Signals */}
          <div className="text-center">
            <div className="inline-flex items-center space-x-6 text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <i className="fas fa-shield-alt text-blue-600"></i>
                <span>Secured by Stripe</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="fas fa-sync-alt text-blue-600"></i>
                <span>Cancel anytime</span>
              </div>
              <div className="flex items-center space-x-2">
                <i className="fas fa-check-circle text-green-600"></i>
                <span>30-day guarantee</span>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-gray-200 py-6">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-xs text-gray-500">
            Questions? Email us at support@digitaltrent.com
          </p>
        </div>
      </div>
    </div>
  );
}